import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "lib/api/api";

const statusColor = {
  SUCCEEDED: "success",
  PENDING: "warning",
  CREATED: "warning",
  FAILED: "error",
  CANCELLED: "default",
  EXPIRED: "default",
};

const PaymentHistory = () => {
  const { t, i18n } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isArabic = i18n.language?.startsWith("ar");

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/payments/history");
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("studentDashboard.paymentHistory.loadError")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString(i18n.language || "en");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress sx={{ color: "#FFC107" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            {t("studentDashboard.paymentHistory.title")}
          </Typography>
          <Typography color="text.secondary">
            {t("studentDashboard.paymentHistory.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={fetchPayments}
          sx={{ borderRadius: 999, fontWeight: 800 }}
        >
          {t("studentDashboard.paymentHistory.refresh")}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!payments.length ? (
        <Paper elevation={0} sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: 3 }}>
          <Typography color="text.secondary">
            {t("studentDashboard.paymentHistory.empty")}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {payments.map((payment) => {
            const status = payment.status || "PENDING";
            const statusUrl = `/payment/success?transactionId=${encodeURIComponent(
              payment.transactionId
            )}&provider=${encodeURIComponent(payment.provider || "")}`;
            return (
              <Paper
                key={payment.transactionId}
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  border: "1px solid #E5E7EB",
                  borderRadius: 3,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography fontWeight={900}>
                        {payment.courseTitle || t("studentDashboard.paymentHistory.courseFallback")}
                      </Typography>
                      <Chip
                        size="small"
                        color={statusColor[status] || "default"}
                        label={t(`studentDashboard.paymentHistory.status.${status}`, {
                          defaultValue: status,
                        })}
                        sx={{ fontWeight: 800 }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      {t("studentDashboard.paymentHistory.transaction", {
                        id: payment.transactionId,
                      })}{" "}
                      · {payment.provider}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(payment.createdAt)}
                    </Typography>
                    {payment.failureReason && (
                      <Typography variant="body2" color="error" sx={{ mt: 0.75 }}>
                        {payment.failureReason}
                      </Typography>
                    )}
                  </Box>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Typography fontWeight={900}>
                      {payment.currency || ""} {Number(payment.amount || 0).toFixed(2)}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      href={statusUrl}
                      sx={{ borderRadius: 999, fontWeight: 800 }}
                    >
                      {t("studentDashboard.paymentHistory.checkStatus")}
                    </Button>
                    {status === "SUCCEEDED" && payment.teacherCourseId && (
                      <Button
                        size="small"
                        variant="contained"
                        href={`/dashboard/enrolled-course/${payment.teacherCourseId}`}
                        sx={{
                          bgcolor: "#FFC107",
                          color: "#111",
                          borderRadius: 999,
                          fontWeight: 900,
                          "&:hover": { bgcolor: "#E6AC00" },
                        }}
                      >
                        {t("studentDashboard.paymentHistory.viewBookedCourse", {
                          defaultValue: "View booked course",
                        })}
                      </Button>
                    )}
                    {payment.checkoutUrl && ["PENDING", "CREATED"].includes(status) && (
                      <Button
                        size="small"
                        variant="contained"
                        href={payment.checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: "#FFC107",
                          color: "#111",
                          borderRadius: 999,
                          fontWeight: 900,
                          "&:hover": { bgcolor: "#E6AC00" },
                        }}
                      >
                        {t("studentDashboard.paymentHistory.continuePayment")}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default PaymentHistory;
